import React from 'react';
import log from '@wix/wix-log';
import type { NextPage } from 'next';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import _ from 'lodash';
import { web } from '@wix/wix-common-aspects';
import { WixDesignSystemProvider, Box } from '@wix/design-system';
import { Service } from '@wix/ambassador-category-services/types';
import { Type } from '@wix/ambassador-marketplace-provider-state/types';
import {
  WixNextApiRequest,
  WixNextApiResponse,
  IParams,
  SEOProps,
  Template as TemplateData,
  EDITOR_TYPES,
  ReviewsInfo,
  ReviewData,
} from '@common/types';
import {
  ENABLE_MARKETPLACE_TEMPLATES_SPEC,
  ENABLE_TEMPLATES_CURRENCY_CONVERSION_SPEC,
  INVALID_ARGUMENT,
  TAX_INCLUSIVE_COUNTRIES,
  TEMPLATE_NOT_FOUND,
} from '@utils/constants';

import { getWixContext } from '@utils/async-store';
import createTemplateSeoAPI from '@services/TemplateSeoAPI';
import { Language } from '@common/types';
import { TemplateContextProvider } from '@components/Contexts/TemplateContext';
import { HeaderContextProvider } from '@components/Contexts/HeaderContext';
import { BiSessionContextProvider } from '@components/Contexts/BiSessionContext';
import { CurrencyContextProvider } from '@components/Contexts/CurrencyContext';
import { ModalsContextProvider } from '@components/Contexts/ModalsContext';
import Template from '@components/Template';
import { viewTemplatePath } from '@components/Hooks/useRouter';
import useMobileMediaQuery from '@components/Hooks/useMobileMediaQuery';

const DynamicNotFoundErrorState = dynamic(
  () => import('@components/Template/ErrorStates/NotFoundErrorState'),
  {
    loading: () => <></>,
  },
);

const DynamicTechnicalErrorState = dynamic(
  () => import('@components/Template/ErrorStates/TechnicalErrorState'),
  {
    loading: () => <></>,
  },
);

const DynamicTemplatePageHeader = dynamic(
  () => import('@components/Template/TemplatePageHeader'),
  {
    loading: () => <></>,
  },
);

function ErrorState({
  error,
  isMobileUserAgent,
}: {
  error: string;
  isMobileUserAgent: boolean;
}) {
  const isMobileView = useMobileMediaQuery(isMobileUserAgent);
  return (
    <>
      <DynamicTemplatePageHeader />
      <Box height={`calc(100vh - ${isMobileView ? '54px' : '72px'})`}>
        {error.includes(INVALID_ARGUMENT) || error === TEMPLATE_NOT_FOUND ? (
          <DynamicNotFoundErrorState />
        ) : (
          <DynamicTechnicalErrorState />
        )}
      </Box>
    </>
  );
}

const TemplatePage: NextPage<{
  template: TemplateData;
  reviewsInfo: ReviewsInfo;
  purchaseCount: number;
  services: Array<Service>;
  categoriesForHeader: Array<Service> | undefined;
  language: string;
  languages: Array<Language>;
  enabled: boolean;
  isRenderedInModal: boolean;
  isMobileUserAgent: boolean;
  error: string;
  host: string;
  seo: SEOProps;
  isEligibleToGiveReview: boolean;
  userAlreadyLeftReview: boolean;
  userCurrency: string;
  userReviewForTemplate: ReviewData | null;
  currencyConversionRate: number;
  taxRate: number;
}> = ({
  template,
  reviewsInfo,
  purchaseCount,
  services,
  categoriesForHeader,
  languages,
  language,
  isMobileUserAgent,
  host,
  error,
  seo,
  isEligibleToGiveReview,
  userAlreadyLeftReview,
  userCurrency,
  userReviewForTemplate,
  currencyConversionRate,
  taxRate,
}) => {
  const isProd = process.env.NODE_ENV === 'production';

  const TemplateHeader = () => (
    <Head>
      <title>{seo.title}</title>
      {_.map(seo.links, (attr, index) =>
        attr.rel === 'canonical' ? null : (
          <link
            hrefLang={attr.hrefLang ? attr.hrefLang : 'x-default'}
            rel={attr.rel}
            href={attr.href}
            key={`lang-${index}`}
          />
        ),
      )}
      {_.map(seo.metaTags, (attr, index) => (
        <meta {...attr} key={`meta-${index}`} />
      ))}
      <link
        rel="canonical"
        href={_.get(_.find(seo.links, { rel: 'canonical' }), 'href')}
      ></link>
      {template?.id && (
        <link
          rel="prefetch"
          href={`${viewTemplatePath}/${template?.id}`}
          as="document"
        ></link>
      )}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: seo.structureData }}
      ></script>
      {isProd && (
        <script dangerouslySetInnerHTML={{ __html: seo.gtm }}></script>
      )}
    </Head>
  );

  const showErrorState = error !== undefined;

  return (
    <>
      <TemplateHeader />
      <BiSessionContextProvider>
        <CurrencyContextProvider
          currencyData={{
            currencyCode: userCurrency,
            conversionRate: currencyConversionRate,
          }}
          taxRate={taxRate}
        >
          <HeaderContextProvider
            services={categoriesForHeader || services}
            host={host}
          >
            <ModalsContextProvider host={host}>
              <TemplateContextProvider
                language={language}
                languages={languages}
                reviewsInfo={reviewsInfo}
                purchaseCount={purchaseCount}
                template={template}
                isMobileUserAgent={isMobileUserAgent}
                isEligibleToGiveReview={isEligibleToGiveReview}
                userAlreadyLeftReview={userAlreadyLeftReview}
                userReviewForTemplate={userReviewForTemplate}
              >
                <WixDesignSystemProvider features={{ newColorsBranding: true }}>
                  {showErrorState ? (
                    <ErrorState
                      error={error}
                      isMobileUserAgent={isMobileUserAgent}
                    />
                  ) : (
                    <Template />
                  )}
                </WixDesignSystemProvider>
              </TemplateContextProvider>
            </ModalsContextProvider>
          </HeaderContextProvider>
        </CurrencyContextProvider>
      </BiSessionContextProvider>
    </>
  );
};

export async function getServerSideProps({
  req,
  res,
  query,
  params,
}: {
  req: WixNextApiRequest;
  res: WixNextApiResponse;
  query: IParams;
  params: any;
}) {
  const wixContext = getWixContext();
  const petriServiceAPI = wixContext.serviceFactory.petriServiceAPI();
  const isTemplatesMarketplaceExperimentEnabled =
    await petriServiceAPI.isExperimentEnabled(
      ENABLE_MARKETPLACE_TEMPLATES_SPEC,
    );
  if (!isTemplatesMarketplaceExperimentEnabled) {
    return {
      redirect: {
        permanent: false,
        destination: 'https://www.wix.com/marketplace',
      },
    };
  }

  const { id } = params;
  const host = `https://${req.get('host')}`;

  const servicesAPI = wixContext.serviceFactory.servicesAPI();
  const reviewsAPI = wixContext.serviceFactory.reviewsAPI();
  const providersAPI = wixContext.serviceFactory.providersAPI();
  const languagesAPI = wixContext.serviceFactory.languagesAPI();
  const templatesAPI = wixContext.serviceFactory.templatesAPI();
  const providerStatesAPI = wixContext.serviceFactory.providerStatesAPI();
  const templateListingAPI = wixContext.serviceFactory.templateListingAPI();
  const transactionsServiceAPI =
    wixContext.serviceFactory.transactionsServiceAPI();
  const currencyAPI = wixContext.serviceFactory.currencyAPI();
  const taxCalculationAPI = wixContext.serviceFactory.taxCalculationAPI();
  const isCurrencyConversionExperimentEnabled =
    await petriServiceAPI.isExperimentEnabled(
      ENABLE_TEMPLATES_CURRENCY_CONVERSION_SPEC,
    );

  const language = req.aspects?.get(web.Language) || 'en';
  const url = req.aspects?.get(web.Url);
  const geo = req.aspects?.get(web.Geo);
  const alpha2CountryCode = geo?.['2lettersCountryCode'];
  let services, categoriesForHeader;

  try {
    const template = await templateListingAPI.getTemplateById(id);
    let reviewsInfo,
      creatorDetails,
      seo,
      languages,
      providerState,
      partnerProfileDetails,
      userReviewForTemplate,
      isEligibleToGiveReview = false;

    const publicTemplate = await templatesAPI.getPublicTemplateById(
      template.templateId,
    );

    if (!publicTemplate) {
      throw Error('publicTemplate not found');
    }

    const hasVeloOrBlocks =
      publicTemplate.template?.publicOptions?.includesCode;

    const [
      _partnerProfileDetails,
      _providerState,
      _isEligibleToGiveReview,
      _services,
      _categoriesForHeader,
    ] = await Promise.allSettled([
      providersAPI.getPartnerProfileById(template!.accountId),
      providerStatesAPI.getProviderState(template!.accountId),
      transactionsServiceAPI.hasTransactionInLastNDays(id, 100),
      servicesAPI.getAllServicesWithHierarchy({
        withPathName: true,
        withTranslatedKey: true,
        language: _.isEmpty(language) ? 'en' : (language as string),
      }),
      servicesAPI.getCategoriesForHeader({
        withPathName: true,
        withTranslatedKey: true,
        language: _.isEmpty(language) ? 'en' : (language as string),
      }),
    ]);

    if (_partnerProfileDetails.status === 'fulfilled') {
      partnerProfileDetails = _partnerProfileDetails.value;
      log('template')
        .withAspects(req.aspects)
        .info('getPartnerDetails fulfilled', {
          partnerProfileDetails,
        });
    } else if (_.isUndefined(partnerProfileDetails)) {
      log('template')
        .withAspects(req.aspects)
        .info('getPartnerDetails rejected', {
          message: _partnerProfileDetails.reason,
        });
    }
    if (_providerState.status === 'fulfilled') {
      providerState = _providerState.value;
    } else {
      log('template').withAspects(req.aspects).info('getProviderState', {
        message: _providerState.reason,
      });
    }
    if (_isEligibleToGiveReview.status === 'fulfilled') {
      isEligibleToGiveReview = _isEligibleToGiveReview.value;
    } else {
      log('template').withAspects(req.aspects).info('getUserTransactions', {
        message: _isEligibleToGiveReview.reason,
      });
    }
    if (_services.status === 'fulfilled') {
      services = _services.value;
    } else {
      log('template')
        .withAspects(req.aspects)
        .info('getAllServicesWithHierarchy', {
          message: _services.reason,
        });
    }
    if (_categoriesForHeader.status === 'fulfilled') {
      categoriesForHeader = _categoriesForHeader.value;
    } else {
      log('template').withAspects(req.aspects).info('getCategoriesForHeader', {
        message: _categoriesForHeader.reason,
      });
    }

    if (!_.isUndefined(template)) {
      const [_languages, reviews] = await Promise.all([
        languagesAPI.getLanguages(),
        reviewsAPI.queryReviews(id),
      ]);

      languages = _languages;
      reviewsInfo = {
        reviews,
        total: template.performance?.reviewsCount ?? 0,
        average: template.performance?.reviewsAverageScore ?? 0,
      };

      const creatorIsActiveInMarketplace =
        _.get(providerState, 'statusType') === Type.PUBLISHED;
      creatorDetails = {
        ...(creatorIsActiveInMarketplace
          ? { slug: _.get(partnerProfileDetails, 'slug') }
          : {
              businessWebsite: _.get(
                partnerProfileDetails,
                'professionalInformation.businessWebsite',
              ),
            }),
        email: _.get(
          partnerProfileDetails,
          'userDetails.email',
          _.get(partnerProfileDetails, 'professionalInformation.email'),
        ),
        profileImage: _.get(
          partnerProfileDetails,
          'professionalInformation.profileImage',
        ),
        creatorIsActiveInMarketplace,
        businessName: _.get(
          partnerProfileDetails,
          'professionalInformation.businessName',
        ),
      };
      const templateSeoAPI = await createTemplateSeoAPI({
        language,
        url,
        reviewsInfo,
        template,
        businessName: creatorDetails.businessName,
      });
      seo = {
        title: `${template.title} | Wix Marketplace | Wix.com`,
        metaTags: templateSeoAPI.getPageMetaTags(),
        links: templateSeoAPI.getPageLinkTags(),
        gtm: templateSeoAPI.getProfileGTM(),
        structureData: JSON.parse(
          JSON.stringify(templateSeoAPI.getTemplateStructureData()),
        ),
      };
    }

    if (_.isUndefined(template)) {
      return {
        props: {
          error: TEMPLATE_NOT_FOUND,
          seo: {
            title: `Wix Templates Marketplace`,
          },
          services,
          categoriesForHeader,
        },
      };
    }

    try {
      userReviewForTemplate = await reviewsAPI.getUserReviewForTemplate(
        reviewsInfo?.reviews,
      );
    } catch (e: any) {
      log('template')
        .withAspects(req.aspects)
        .info('getUserReviewForTemplate', {
          message: e.message,
        });
    }

    const { userCurrency, currencyConversionRate } =
      isCurrencyConversionExperimentEnabled
        ? await currencyAPI.getCurrencyAndConversionRateSupportedByEcom({
            alpha2CountryCode,
          })
        : await currencyAPI.getDefaultCurrencyAndConversionRate();

    // returns taxRate = 0 for non included countries
    let countryTaxRate = 0;
    const countryCode = geo?.['2lettersCountryCode'] || '';
    try {
      if (
        TAX_INCLUSIVE_COUNTRIES.includes(countryCode) &&
        isCurrencyConversionExperimentEnabled
      ) {
        const { taxRate } = await taxCalculationAPI.getTaxRate(
          countryCode,
          userCurrency,
        );
        countryTaxRate = taxRate ?? 0;
      }
    } catch (error: any) {
      log('template').withAspects(req.aspects).info('getCountryTaxRate', {
        message: error.message,
      });
    }

    const { purchaseCount, ...restOfTemplate } = template;

    const props = {
      id,
      template: JSON.parse(
        JSON.stringify({
          ...restOfTemplate,
          platform: EDITOR_TYPES.STUDIO,
          partnerProfileDetails: creatorDetails,
          hasVeloOrBlocks,
        }),
      ),
      reviewsInfo: JSON.parse(JSON.stringify(reviewsInfo)),
      purchaseCount,
      isEligibleToGiveReview,
      userAlreadyLeftReview: !!userReviewForTemplate,
      ...(userReviewForTemplate && { userReviewForTemplate }),
      languages,
      services,
      categoriesForHeader,
      host,
      seo,
      userCurrency,
      currencyConversionRate,
      taxRate: countryTaxRate,
    };

    return {
      props,
    };
  } catch (e: any) {
    const { message, stack } = e;
    log('template')
      .withAspects(req.aspects)
      .error(`error w/ template`, { message, stack });
    return {
      props: {
        message,
        stack,
        seo: {
          title: `Wix Templates Marketplace`,
        },
        error: e.message,
        services,
        categoriesForHeader,
      },
    };
  }
}

export default TemplatePage;
